<script lang="ts">
  import { onMount, createEventDispatcher } from "svelte";
  import { errorsIf, formField } from "../";
  import { clickOutside } from "../../clickOutside";
  import FieldLable from "./FieldLabel.svelte";

  interface Option {
    text: string;
    value: any;
    selected?: boolean;
  }

  export let name: string | undefined = undefined;
  export let key: string;
  export let options: Option[] = [];
  export let autofocus = false;
  export let allowFilter = false;
  // Validation
  export let required = false;
  export let placeholder = "";
  let dropOpen: boolean = false;
  let inputEl: HTMLInputElement;
  let dropdownContainer: HTMLDivElement;
  let filter: string = "";
  let filteredOptions: Option[] = options;
  const dispatcher = createEventDispatcher();

  const field = formField(key, name);
  // Reactive validations
  $: $field.isPending = dropOpen;
  $: $field.errors = errorsIf(required && !$field.value && "is required");

  // Following is for keyboard support
  $: selectedOption = filteredOptions.find((o) => o.selected);
  $: if (selectedOption && dropdownContainer) {
    const optionElement: HTMLAnchorElement | null = dropdownContainer.querySelector(`[data-id='${selectedOption.value}']`);
    if (optionElement) {
      const top = optionElement.offsetTop;
      window.requestAnimationFrame(() => {
        optionElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      });
    }
  }
  // End of keyboard support

  function handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case "Tab":
        if (dropOpen) {
          event.stopPropagation();
          event.preventDefault();
          if (event.shiftKey) {
            decreaseSelectedOption();
          } else {
            increaseSelectedOption();
          }
          return false;
        }
        break;
      case "ArrowDown":
        event.stopPropagation();
        event.preventDefault();
        if (!dropOpen) {
          dropOpen = true;
        } else {
          increaseSelectedOption();
        }
        return false;
      case "ArrowUp":
        event.stopPropagation();
        event.preventDefault();
        if (dropOpen) {
          decreaseSelectedOption();
          return false;
        }
        break;
      case "Escape":
        event.stopPropagation();
        event.preventDefault();
        if (dropOpen) {
          dropOpen = false;
        } else {
          field.cancelForm();
        }
        return false;
      case "Enter":
        event.stopPropagation();
        event.preventDefault();
        if (dropOpen) {
          const selectedOption = filteredOptions.find((option) => option.selected);
          if (selectedOption) {
            setSelection(selectedOption);
          }
        } else if ($field.value && $field.isDirty) {
          field.submitForm();
        }
        return false;
    }
  }

  $: if (allowFilter) {
    if (filter.length > 0) {
      const selectedOption = options.find((option) => option.text === filter);
      const lowerFilter = filter.toLowerCase();

      filteredOptions = options
        .map((option) => {
          option.selected = option.text === filter;
          return option;
        })
        .filter((option) => option.text.toLowerCase().startsWith(lowerFilter));
      if (!selectedOption) {
        $field.isPending = true;
        dropOpen = true;
      }
    } else {
      $field.value = null;
      filteredOptions = options.map((option) => {
        option.selected = false;
        return option;
      });
    }
  }

  function increaseSelectedOption() {
    let selectedIndex = filteredOptions.findIndex((option) => option.selected) + 1;
    if (selectedIndex === filteredOptions.length) {
      selectedIndex = 0;
    }
    filteredOptions = [
      ...filteredOptions.map((op, index) => {
        op.selected = index === selectedIndex;
        return op;
      })
    ];
  }

  function decreaseSelectedOption() {
    let selectedIndex = filteredOptions.findIndex((option) => option.selected) - 1;
    if (selectedIndex < 0) {
      selectedIndex = filteredOptions.length - 1;
    }
    filteredOptions = [
      ...filteredOptions.map((op, index) => {
        op.selected = index === selectedIndex;
        return op;
      })
    ];
  }

  function setSelection(option: Option) {
    $field.value = option.value;
    $field.isPending = false;
    filter = option.text;
    options = [
      ...options.map((op) => {
        op.selected = option.value === op.value;
        return op;
      })
    ];
    dispatcher("changed", { option });
    dropOpen = false;
  }

  function clickOnSelect() {
    dropOpen = !dropOpen;
  }

  onMount(() => {
    if (autofocus) {
      window.requestAnimationFrame(() => {
        inputEl.select();
      });
    }
  });
</script>

<FieldLable {name} {required} errorMessage={$field.errors.length && $field.isDirty ? $field.errors[0]?.message : undefined}>
  <div class="relative wrapper">
    <div
      disabled={$field.isDisabled}
      class="relative mt-1 transition-all border-gray-300 focus:border-teal-400"
      on:click={clickOnSelect}
      use:clickOutside={() => {
        dropOpen = false;
      }}>
      <div
        class="absolute right-0 z-10 w-8 h-full transform bg-center bg-no-repeat pointer-events-none"
        class:rotate-180={dropOpen}
        style="background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTS40MDQwNjMgMkw0LjEwNDA2IDUuN2MuNC40IDEuMi40IDEuNiAwbDMuNy0zLjdjLjUtLjQuNS0xLjIgMC0xLjYtLjQtLjUtMS4xLS41LTEuNi0uMWwtMi45IDIuOS0yLjktMi45Yy0uNS0uNC0xLjE5OTk5Ny0uNC0xLjU5OTk5NyAwLS4yLjMtLjguOCAwIDEuN3oiIGZpbGw9IiM1MDUwNjAiLz48L3N2Zz4');" />
      <input
        type="text"
        readonly={!allowFilter}
        {placeholder}
        class="relative w-full cursor-default text-13 form-input"
        class:drop-open={dropOpen}
        bind:this={inputEl}
        bind:value={filter}
        on:keydown={handleKeyDown} />

      {#if dropOpen}
        <div
          bind:this={dropdownContainer}
          class="absolute left-0 z-10 w-full p-1 overflow-y-scroll bg-white border border-t-0 rounded rounded-t-none shadow options-container">
          {#each filteredOptions as option (option.value)}
            <a
              href={""}
              data-id={option.value}
              class:bg-teal-200={option.selected}
              class="block option w-full hover:bg-teal-200 rounded p-1 py-1 my-0.5 px-2 text-sm transition-colors focus:outline-none focus:bg-teal-200"
              on:click|stopPropagation|preventDefault={() => setSelection(option)}>
              {option.text}
            </a>
          {:else}
            <div class="px-2 text-xs text-gray-600">
              {#if filter.length}
                No lists matching filter
              {:else}
                You have no lists to copy to
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</FieldLable>

<style>
  .form-input {
    max-height: 80px;
    border-radius: theme("borderRadius.DEFAULT");
  }

  .form-input:focus,
  .form-input:focus-within,
  .form-input:active {
    outline: none;
    box-shadow: 0 0 0 4px #dcf4f1;
  }

  /* Style from https://css-tricks.com/styling-a-select-like-its-2019/ */
  .form-input {
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .options-container {
    max-height: 35vh;
  }
</style>
